import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Modal, Typography, Button, TextField, Skeleton, CircularProgress } from '@ann/web-components';
import {
  ModalWrapper,
  GroupsGridWrapper,
  SelectedItemsContent,
  SelectedItemsAction,
  SelectedGroupsText,
  ModalBodyContent,
  ContentHeader,
  SearchFieldWrapper,
  SelectedItemsRolesWrapper,
  LoadMoreButtonContainer,
  LoadingContainer
} from './AddUsersModals.styled';
import { ModalListCard } from '../../components';
import { useBackendPagination } from '../../../../hooks/useBackendPagination';
import { getRoleList, assignRole } from '../../../../services';
import { useToast } from '../../../../../../../components/toast';

const ITEMS_PER_PAGE = 4;

const AddUsersToRolesModal = ({selectedUsers = [], onClose, isOpen = false}) => {
  const toast = useToast();
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [searchValue, setSearchValue] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const resetRef = useRef(null);

  // Configure fetch function for getRoleList - memoized to avoid recreating on each render
  const fetchRoles = useCallback(async (params) => {
    try {
      const response = await getRoleList(params);
      const statusCode = response.status_code || response.statusCode || 200;
      const responseBody = response.body || [];
      const meta = response.meta || {};

      // Normalize meta to ensure has_next is always present
      const normalizedMeta = {
        ...meta,
        has_next: meta.has_next !== undefined ? meta.has_next : (meta.hasNext || false)
      };

      if (statusCode >= 200 && statusCode < 300) {
        return {
          items: Array.isArray(responseBody) ? responseBody : [],
          meta: normalizedMeta
        };
      } else {
        throw new Error(response.msg_code?.[0]?.description || 'Failed to fetch roles');
      }
    } catch (error) {
      throw error;
    }
  }, []);

  // Use backend pagination hook
  const {
    items: allRoles,
    isLoading,
    error,
    hasMore,
    loadMore,
    reset,
    meta,
  } = useBackendPagination(
    fetchRoles,
    {
      order_by: ["name"],
      order_direction: ["ASC"]
    },
    {
      itemsPerPage: ITEMS_PER_PAGE,
      autoLoadFirstPage: isOpen, // Only auto-load when modal is open
      onError: (error) => {
        const errorMessage = error?.message || 'Error loading roles. Please try again later.';
        toast.error({ 
          message: errorMessage, 
          duration: 4000, 
          canClose: true 
        });
      }
    }
  );

  // Store reset function in ref for use in debounced search
  useEffect(() => {
    resetRef.current = reset;
  }, [reset]);

  // Load roles when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Build search params based on searchValue
  const buildSearchParams = useCallback((keyword) => {
    if (keyword && keyword.trim()) {
      return { search: { keyword: keyword.trim() } };
    }
    return { search: undefined };
  }, []);

  // Helper function to reset modal state
  const resetModalState = useCallback(() => {
    setSelectedRoles(new Set());
    setSearchValue("");
    setIsLoadingMore(false);
  }, []);

  // Update search params when search value changes (with debounce)
  useEffect(() => {
    if (!isOpen) return;
    
    const timeoutId = setTimeout(() => {
      const searchParams = buildSearchParams(searchValue);
      resetRef.current?.({
        ...searchParams,
        order_by: ["name"],
        order_direction: ["ASC"]
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchValue, isOpen, buildSearchParams]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  const handleLoadMore = useCallback(async () => {
    try {
      setIsLoadingMore(true);
      await loadMore();
    } catch (error) {
      // Error already handled in onError callback
    } finally {
      setIsLoadingMore(false);
    }
  }, [loadMore]);

  const handleAddRoles = useCallback(async () => {
    if (selectedRoles.size === 0 || selectedUsers.length === 0 || isAssigning) return;

    try {
      setIsAssigning(true);

      // Extract userUuids from selectedUsers
      // Programación defensiva: soportar ambos formatos (uuid y userUuid)
      const userUuids = selectedUsers
        .map(user => user.uuid || user.userUuid || user.id)
        .filter(Boolean);

      // Extract roleUuids from selectedRoles
      const roleUuids = Array.from(selectedRoles);

      if (userUuids.length === 0) {
        throw new Error("No valid user UUIDs found");
      }

      if (roleUuids.length === 0) {
        throw new Error("No roles selected");
      }

      await assignRole(userUuids, roleUuids);

      toast.success({
        message: `Role${roleUuids.length === 1 ? '' : 's'} assigned successfully to ${selectedUsers.length} ${selectedUsers.length === 1 ? 'user' : 'users'}`,
        canClose: true
      });

      resetModalState();
      onClose?.();
    } catch (error) {
      console.error("Error assigning roles:", error);

      toast.error({
        message: error.message || "Error assigning roles",
        canClose: true
      });
    } finally {
      setIsAssigning(false);
    }
  }, [selectedRoles, selectedUsers, toast, resetModalState, onClose, isAssigning]);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose?.();
  }, [resetModalState, onClose]);

  const handleClearSelection = useCallback(() => {
    setSelectedRoles(new Set());
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value || "");
  }, []);

  const handleRoleToggle = useCallback((roleId) => {
    setSelectedRoles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  }, []);

  // Transform API roles to card format
  const listRolesCards = useMemo(() => {
    return allRoles.map((role) => ({
      id: role.roleUuid || role.id,
      title: role.name || role.title,
      description: role.description || ""
    }));
  }, [allRoles]);

  // Get all selected roles titles - optimized with Map for O(1) lookup
  const selectedRoleTitles = useMemo(() => {
    if (selectedRoles.size === 0) return [];
    
    const rolesMap = new Map(allRoles.map(r => [r.roleUuid || r.id, r.name || r.title]));
    
    return Array.from(selectedRoles)
      .map(roleId => rolesMap.get(roleId) || roleId);
  }, [selectedRoles, allRoles]);

  // Calculate total items from meta
  const totalItems = meta?.total_items || allRoles.length;

  // Reusable loading spinner
  const loadingSpinner = (
    <LoadingContainer>
      <CircularProgress size="xl" />
    </LoadingContainer>
  );

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={handleClose} size="medium">
        <Modal.Header>
          <Modal.Header.Title>
              Assign roles
          </Modal.Header.Title>
          <Modal.Header.Description>
            <Typography category="body" size="medium" weight="regular" color="body">
                Assign roles to define what tools and features users can access. You can edit these functions in the next steps.
            </Typography>
          </Modal.Header.Description>
        </Modal.Header>

        <Modal.Body>
          <ModalBodyContent>
            <ContentHeader id={'header-c'}>
              <SearchFieldWrapper>
                <TextField
                  iconLeft="search"
                  onChange={handleSearchChange}
                  size="small"
                  placeholder="Search"
                  value={searchValue}
                  disabled={isLoading}
                />
              </SearchFieldWrapper>

              <Typography category="body" size="medium" weight="regular" color="body">
                Showing {listRolesCards.length} of {totalItems} {totalItems === 1 ? 'role' : 'roles'}
              </Typography>
            </ContentHeader>

            <GroupsGridWrapper>
              {isLoading && listRolesCards.length === 0 ? (
                loadingSpinner
              ) : (
                <>
                  {/* Show CircularProgress when searching/filtering (not Load More) */}
                  {isLoading && !isLoadingMore && listRolesCards.length > 0 && loadingSpinner}

                  {/* Hide cards when searching, show when loading more */}
                  {(!isLoading || isLoadingMore) && (
                    <>
                      {listRolesCards.map((role) => (
                        <ModalListCard
                          key={role.id}
                          id={role.id}
                          title={role.title}
                          description={role.description}
                          isChecked={selectedRoles.has(role.id)}
                          onChange={() => handleRoleToggle(role.id)}
                        />
                      ))}
                    </>
                  )}
                  
                  {/* Show skeletons when loading more */}
                  {isLoadingMore && (
                    <>
                      {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                        <Skeleton key={`loading-skeleton-${i}`} height="72px" width="100%" />
                      ))}
                    </>
                  )}

                  {/* Show Load More button when not loading and has more */}
                  {!isLoading && !isLoadingMore && hasMore && (
                    <LoadMoreButtonContainer>
                      <Button
                        size="medium"
                        type="tertiary"
                        onClick={handleLoadMore}
                        label="Load More"
                      />
                    </LoadMoreButtonContainer>
                  )}
                </>
              )}
            </GroupsGridWrapper>

            <SelectedItemsRolesWrapper selectedRoles={selectedRoles.size}>
              <SelectedItemsContent>
                <Typography category="subtitle" size="small" weight="bold" >
                  Selected roles ({selectedRoles.size})
                </Typography>
                <SelectedGroupsText>
                  {selectedRoleTitles.join(" · ")}
                </SelectedGroupsText>
              </SelectedItemsContent>
              <SelectedItemsAction>
                <Button 
                  label="Clear selection" 
                  size="small" 
                  type="tertiary" 
                  maxCharacters={40} 
                  disableEffects={true}
                  onClick={handleClearSelection}
                />
              </SelectedItemsAction>
            </SelectedItemsRolesWrapper>

          </ModalBodyContent>
        </Modal.Body>
        <Modal.Actions align="end">
          <Modal.Button
            label="Cancel"
            onClick={handleClose}
            type="secondary"
            disabled={isAssigning}
          />
          <Modal.Button
            label={isAssigning ? "Assigning..." : `Add ${selectedRoles.size} ${selectedRoles.size === 1 ? 'role' : 'roles'}`}
            onClick={handleAddRoles}
            disabled={selectedRoles.size === 0}
          />
        </Modal.Actions>
      </Modal>
    </ModalWrapper>
  );
}

export default AddUsersToRolesModal;